import { useIsMutating } from '@tanstack/react-query';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import useFetchPropertyChannelSettings from 'components/domain/channelSettings/useFetchPropertyChannelSettings';
import { PROPERTIES_SELECTION } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.constants';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import ModalFormCancelButton from 'components/modal/form/ModalFormCancelButton';
import ModalFormSubmitButton from 'components/modal/form/ModalFormSubmitButton';
import {
  AirbnbFeeType,
  CleaningFeeCollection,
  FeeAmountTypeEnum,
  FeeConditionEnum,
  FeeScopeEnum,
  FeeTypeEnum,
  HostfullyFeeType,
  PropertyInternalFee,
} from 'models/PropertyFees';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Currency } from 'models/Currency';
import { Property } from 'models/Properties';
import PropertyFeeModalFields from './PropertyFeeModalFields';
import {
  PropertyFeeFormSchemaType,
  propertyFeeSchema,
} from './PropertyFeeModal.schema';
import PropertyFeeModalPlaceholder from './PropertyFeeModalPlaceholder';
import { BY_LENGTH_OF_STAY_SCOPE_OPTION } from './PropertyFeeModal.constants';
import { getBookingPercentAppliesToFromChargeBases } from './PropertyFeeModal.utils';

export const ADD_PROPERTY_FEE_MODAL_ID = 'add-property-fee-modal';
export const EDIT_PROPERTY_FEE_MODAL_ID = 'edit-property-fee-modal';

const PropertyFeeModal = ({
  fee,
  handleSubmit,
  queryKey,
  propertyCurrency,
  property,
  isLoadingProperty,
}: {
  fee?: PropertyInternalFee;
  handleSubmit: (data: PropertyFeeFormSchemaType) => void;
  queryKey: string[];
  propertyCurrency: Currency;
  property: Property;
  isLoadingProperty: boolean;
}) => {
  const { t } = useTranslation();
  const { isFetching: isFetchingAgencyChannels } =
    useFetchAgencyChannelSettings();
  const { isFetching: isFetchingPropertyChannels } =
    useFetchPropertyChannelSettings(property?.uid);
  const isMutating = useIsMutating(queryKey);

  const isEdit = !!fee;
  const isTax = fee?.type === FeeTypeEnum.TAX;
  const isFee = fee?.type === FeeTypeEnum.CUSTOM;
  const isSubmitting = isMutating > 0;

  const getFormDefaultValues = () => {
    const addInitialValues = {
      airbnbMappingType: undefined,
      airbnbUnitMappingType: undefined,
      amount: '',
      amountType: FeeAmountTypeEnum.AMOUNT,
      appliesTo: PROPERTIES_SELECTION.SELECTED_PROPERTIES,
      appliesToProperties: [
        {
          label: property.name,
          value: property.uid,
          isFixed: true,
        },
      ],
      appliesToHostfully: 'NO',
      appliesToStatus: undefined,
      appliesToTags: undefined,
      bookingMappingType: undefined,
      bookingPercentAppliesTo: '',
      cleaningFeeCollectionMappingType: undefined,
      hvmbMappingType: undefined,
      name: '',
      rollsIntoRent: 'NO',
      optional: 'NO',
      type: FeeTypeEnum.CUSTOM,
      fee: {
        taxationRate: 0,
      },
      tax: {
        conditions: {
          type: FeeConditionEnum.NONE,
          lengthOfStayType: FeeConditionEnum.EXEMPT_LONG_STAY,
          lengthOfStayValue: '',
          firstNightsApplying: 7,
        },
      },
      scope: FeeScopeEnum.PER_STAY,
      vrboMappingType: undefined,
    };

    const getEditInitialValues = () => {
      const getAppliesToProperties = () => {
        const properties =
          fee?.metadata?.propertiesThatShareFee?.map(
            ({ left: uid, right: name }) => ({
              label: name,
              value: uid,
              isFixed: uid === property?.uid,
            }),
          ) ?? [];

        // Adding because the current property is not included in the propertiesThatShareFee
        return [
          {
            label: property?.name,
            value: property?.uid,
            isFixed: true,
          },
          ...properties,
        ];
      };

      const getConditions = () => {
        const {
          condition,
          conditionsLOSDays,
          longTermStayExemption,
          firstNightsApplying,
        } = fee?.condition ?? {};

        switch (condition) {
          case FeeConditionEnum.NONE:
            return {
              type: FeeConditionEnum.NONE,
              lengthOfStayType: null,
              lengthOfStayValue: '',
              firstNightsApplying: null,
            };

          case FeeConditionEnum.EXCLUSIVE_LONG_STAY:
            return {
              type: BY_LENGTH_OF_STAY_SCOPE_OPTION,
              lengthOfStayType: FeeConditionEnum.EXCLUSIVE_LONG_STAY,
              lengthOfStayValue: conditionsLOSDays,
              firstNightsApplying: null,
            };

          case FeeConditionEnum.EXEMPT_LONG_STAY:
            return {
              type: BY_LENGTH_OF_STAY_SCOPE_OPTION,
              lengthOfStayType: FeeConditionEnum.EXEMPT_LONG_STAY,
              lengthOfStayValue: longTermStayExemption,
              firstNightsApplying: null,
            };

          case FeeConditionEnum.BY_NIGHT:
            return {
              type: FeeConditionEnum.BY_NIGHT,
              lengthOfStayType: null,
              lengthOfStayValue: '',
              firstNightsApplying,
            };

          default:
            return {
              type: FeeConditionEnum.NONE,
              lengthOfStayType: null,
              lengthOfStayValue: '',
              firstNightsApplying: null,
            };
        }
      };

      return {
        amount: fee?.amount,
        amountType: fee?.amountType,
        uid: fee?.uid,
        airbnbMappingType: fee?.airbnbType,
        airbnbUnitMappingType: fee?.airbnbUnitType,
        cleaningFeeCollectionMappingType:
          fee?.airbnbType === AirbnbFeeType.PASS_THROUGH_CLEANING_FEE &&
          fee?.offline
            ? CleaningFeeCollection.OFFLINE
            : CleaningFeeCollection.AIRBNB,
        type: fee?.type,
        name: fee?.name,
        optional: fee?.optional ? 'YES' : 'NO',
        appliesToHostfully:
          fee?.hostfullyType === HostfullyFeeType.DEFAULT ? 'YES' : 'NO',
        appliesToStatus: undefined,
        appliesToTags: undefined,
        rollsIntoRent: fee?.rollsIntoRent ? 'YES' : 'NO',
        appliesTo: fee?.metadata?.appliesToAllProperties
          ? PROPERTIES_SELECTION.ALL_PROPERTIES
          : PROPERTIES_SELECTION.SELECTED_PROPERTIES,
        appliesToProperties: getAppliesToProperties(),
        scope: fee?.scope ? fee?.scope : FeeScopeEnum.PER_STAY,
        vrboMappingType: fee?.vrboType,
        hvmbMappingType: fee?.hvmiType,
        bookingMappingType: fee?.bookingDotComType,
        bookingPercentAppliesTo: getBookingPercentAppliesToFromChargeBases(
          fee?.bookingDotComChargeBases,
        ),
        fee: isFee
          ? {
              taxationRate: fee?.taxationRate,
            }
          : addInitialValues.fee,
        tax: isTax
          ? {
              conditions: getConditions(),
            }
          : addInitialValues.tax,
      };
    };

    return isEdit ? getEditInitialValues() : addInitialValues;
  };

  if (
    isLoadingProperty ||
    isFetchingAgencyChannels ||
    isFetchingPropertyChannels
  ) {
    return <PropertyFeeModalPlaceholder isEdit={isEdit} />;
  }

  return (
    <div>
      <FormWithProvider
        horizontal
        onSubmit={handleSubmit}
        defaultValues={getFormDefaultValues()}
        resolver={propertyFeeSchema({ t })}
        noValidate
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit
              ? t(
                  'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.editFeeModalTitle',
                )
              : t(
                  'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.addFeeModalTitle',
                )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormFieldset disabled={isSubmitting}>
            <PropertyFeeModalFields
              propertyCurrency={propertyCurrency}
              propertyUid={property?.uid}
            />
          </FormFieldset>
        </Modal.Body>
        <Modal.Footer>
          <ModalFormCancelButton disabled={isSubmitting} />
          <ModalFormSubmitButton disabled={isSubmitting} />
        </Modal.Footer>
      </FormWithProvider>
    </div>
  );
};

PropertyFeeModal.defaultProps = {
  fee: undefined,
};

export default PropertyFeeModal;
